import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GBP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, PoundSterling, Users, CalendarCheck, LogOut, AlertTriangle } from "lucide-react";
import { Companies, UsersPanel, PaymentsPanel, PlatformSettings, AuditPanel } from "@/components/AdminPanels";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid={testid}>
      <div className="h-10 w-10 rounded-xl bg-[#F3EFEA] flex items-center justify-center text-[#B07E4F]"><Icon className="h-5 w-5" /></div>
      <div className="mt-3 text-2xl font-bold font-mono">{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#18181B] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#D4A373] flex items-center justify-center text-stone-900 font-bold">G</div>
            <div><div className="font-bold">Get Booked In</div><div className="text-[11px] text-stone-400">Super admin</div></div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-300 hidden sm:inline">{user?.email}</span>
            <Button data-testid="admin-logout" variant="ghost" size="sm" onClick={doLogout} className="text-stone-300 hover:text-white hover:bg-stone-800 rounded-full"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-extrabold text-3xl">Super admin</h1>
        {stats && (
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="admin-stat-businesses" icon={Building2} label={`Companies · ${stats.by_status.trial} trial · ${stats.by_status.active} paying`} value={stats.businesses} />
            <Stat testid="admin-stat-mrr" icon={PoundSterling} label="Monthly recurring revenue" value={GBP(stats.mrr)} />
            <Stat testid="admin-stat-users" icon={Users} label="Users" value={stats.users} />
            <Stat testid="admin-stat-appointments" icon={CalendarCheck} label={`Bookings · ${GBP(stats.client_payments)} client payments`} value={stats.total_appointments} />
          </div>
        )}
        {stats?.failed_invoices > 0 && <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{stats.failed_invoices} failed subscription payments — see Payments</p>}
        <Tabs defaultValue="companies" className="mt-6">
          <TabsList className="rounded-full flex-wrap h-auto">
            {[["companies", "Companies"], ["users", "Users"], ["payments", "Payments"], ["settings", "Pricing & connectors"], ["audit", "Activity log"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-full">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="companies" className="mt-5"><Companies onChanged={load} /></TabsContent>
          <TabsContent value="users" className="mt-5"><UsersPanel /></TabsContent>
          <TabsContent value="payments" className="mt-5"><PaymentsPanel /></TabsContent>
          <TabsContent value="settings" className="mt-5"><PlatformSettings /></TabsContent>
          <TabsContent value="audit" className="mt-5"><AuditPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
